import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Video,
  Mic2,
  DollarSign,
  TrendingUp,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  ShoppingCart,
  Loader2,
} from 'lucide-react';
import { adminAPI } from '@/services/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default stats on error
      setStats({
        total_users: 0,
        total_webinars: 0,
        total_speakers: 0,
        total_orders: 0,
        revenue_this_month: 0,
        registrations_this_month: 0,
        new_users_this_month: 0,
        active_webinars: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-700" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'bg-blue-500',
      change: stats?.new_users_this_month || 0,
      changeLabel: 'new this month',
      link: '/admin/users',
    },
    {
      title: 'Total Webinars',
      value: stats?.total_webinars || 0,
      icon: Video,
      color: 'bg-purple-500',
      change: stats?.active_webinars || 0,
      changeLabel: 'active',
      link: '/admin/webinars',
    },
    {
      title: 'Speakers',
      value: stats?.total_speakers || 0,
      icon: Mic2,
      color: 'bg-green-500',
      change: null,
      link: '/admin/speakers',
    },
    {
      title: 'Revenue',
      value: `$${(stats?.revenue_this_month || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-emerald-500',
      change: stats?.total_orders || 0,
      changeLabel: 'total orders',
      link: '/admin/orders',
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Welcome Banner */}
      <div className="bg-zinc-950 rounded-2xl p-8 text-white">
        <h2 className="font-heading text-2xl font-bold mb-2">Welcome to Admin Dashboard</h2>
        <p className="text-zinc-400">Manage your GRC Educators platform from here</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, idx) => (
          <Link key={idx} to={stat.link}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-zinc-200 rounded-xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-zinc-400" />
                </div>
                <h3 className="text-sm text-zinc-500 mb-1">{stat.title}</h3>
                <p className="text-3xl font-bold text-zinc-950">{stat.value}</p>
                {stat.change !== null && (
                  <p className="text-sm text-zinc-500 mt-2">
                    <span className="text-green-600 font-medium">+{stat.change}</span> {stat.changeLabel}
                  </p>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card className="border-zinc-200 rounded-xl">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link 
              to="/admin/webinars" 
              className="flex items-center gap-3 p-4 bg-zinc-50 rounded-xl hover:bg-zinc-100 transition-colors"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Video className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-zinc-800">Create New Webinar</p>
                <p className="text-sm text-zinc-500">Schedule a new training session</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-zinc-400" />
            </Link>
            
            <Link 
              to="/admin/speakers" 
              className="flex items-center gap-3 p-4 bg-zinc-50 rounded-xl hover:bg-zinc-100 transition-colors"
            >
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Mic2 className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-zinc-800">Add Speaker</p>
                <p className="text-sm text-zinc-500">Onboard a new expert speaker</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-zinc-400" />
            </Link>
            
            <Link 
              to="/admin/users" 
              className="flex items-center gap-3 p-4 bg-zinc-50 rounded-xl hover:bg-zinc-100 transition-colors"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-zinc-800">Manage Users</p>
                <p className="text-sm text-zinc-500">View and manage user accounts</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-zinc-400" />
            </Link>
          </CardContent>
        </Card>

        {/* System Status */}
        <Card className="border-zinc-200 rounded-xl">
          <CardHeader>
            <CardTitle className="font-heading text-lg">System Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="font-medium text-green-800">System Status</span>
                </div>
                <Badge className="bg-green-500 text-white">Operational</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-50 rounded-xl">
                  <p className="text-sm text-zinc-500 mb-1">Active Sessions</p>
                  <p className="text-2xl font-bold text-zinc-950">
                    {stats?.registrations_this_month || 0}
                  </p>
                </div>
                <div className="p-4 bg-zinc-50 rounded-xl">
                  <p className="text-sm text-zinc-500 mb-1">Total Industries</p>
                  <p className="text-2xl font-bold text-zinc-950">
                    {stats?.total_industries || 10}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-emerald-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-emerald-700" />
                  <span className="font-medium text-emerald-800">Upcoming Webinars</span>
                </div>
                <p className="text-sm text-emerald-700">
                  {stats?.upcoming_webinars || 0} webinars scheduled this week
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
